<script setup lang="ts">
import { computed } from 'vue';
import SplitView from '@/components/SplitView.vue';
import DockContainer from '@/components/DockContainer.vue';
import PanelSlot from '@/components/PanelSlot.vue';
import PanelPortal from '@/components/PanelPortal.vue';
import type { PanelSystem } from '@/composables/use-panels';

const props = defineProps<{
	panels: PanelSystem;
}>();

const left = computed(() => props.panels.regions.value.left);
const center = computed(() => props.panels.regions.value.center);
const right = computed(() => props.panels.regions.value.right);
const bottom = computed(() => props.panels.regions.value.bottom);

// whether a panel is the active tab in its leaf node
const isActive = (uri: string, nodeId: string) => {
	const leaf = props.panels.findLeafById(nodeId);
	if (!leaf) return false;
	return props.panels.activeUriForNode(leaf.leaf) === uri;
};
</script>

<template>
	<div class="flex size-full px-2 pb-2 gap-2 overflow-hidden">
		<!-- left dock -->
		<DockContainer :region-root="left" :panels="panels" region="left" />

		<!-- center column: center panels + bottom dock -->
		<div class="flex min-w-0 flex-1 flex-col gap-2 overflow-hidden">
			<!-- center panels -->
			<div class="min-h-0 flex-1 overflow-hidden">
				<SplitView :node="center.root" :panels="panels" region="center">
					<template #empty>
						<span>no files open</span>
					</template>
				</SplitView>
			</div>

			<!-- bottom dock -->
			<DockContainer :region-root="bottom" :panels="panels" region="bottom" />
		</div>

		<!-- right dock -->
		<DockContainer :region-root="right" :panels="panels" region="right" />
	</div>

	<!-- all panels rendered once, portaled to their leaf node's content div.
	     PanelPortal re-resolves the target selector after every DOM update,
	     surviving target element recreation during tree restructuring. -->
	<PanelPortal
		v-for="panel in panels.allPanels.value"
		:key="panel.uri"
		:to="`#panel-content-${panel.nodeId}`"
		:version="panels.treeVersion.value"
	>
		<div
			class="absolute inset-0"
			:class="isActive(panel.uri, panel.nodeId) ? 'z-10' : 'z-0 hidden'"
			@focusin="panels.focus(panel.uri)"
		>
			<PanelSlot :panel="panel" :panels="panels" />
		</div>
	</PanelPortal>
</template>
